import React, { createContext, useCallback, useRef, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { MY_PERMISSIONS_QUERY } from '../apollo/operations/permissions.operations';
import { useAuth } from '../hooks/useAuth';

// ─── Types ────────────────────────────────────────────────────────────────────

export type FormMode = 'get' | 'add' | 'edit' | 'delete' | 'download' | 'bulkImport';

const FORM_MODE_BITS: Record<FormMode, number> = {
  get:        1,
  add:        2,
  edit:       4,
  delete:     8,
  download:   16,
  bulkImport: 32,
};

export interface PermissionsContextValue {
  /** Returns true if the current user has the given permission bit for the given menu code. */
  hasPermission: (menuCode: string, mode: FormMode) => boolean;
  /** True while the initial permissions query is in flight. */
  isLoading: boolean;
  /** Call after changing the current user's own permission group to force a reload. */
  refetch: () => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

export const PermissionsContext = createContext<PermissionsContextValue>({
  hasPermission: () => false,
  isLoading:     true,
  refetch:       () => undefined,
});

// ─── Provider ─────────────────────────────────────────────────────────────────

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();

  // Map<menuCode, permissionBit> — stored in a ref to avoid re-rendering every consumer
  // on each permission check.  A separate boolean state drives the loading indicator.
  const permMap = useRef(new Map<string, number>());
  const [loaded, setLoaded] = useState(false);

  const { data, loading, refetch: apolloRefetch } = useQuery(MY_PERMISSIONS_QUERY, {
    skip:        !isAuthenticated,
    fetchPolicy: 'network-only',
  });

  // Populate the permission map whenever new data arrives from the query
  React.useEffect(() => {
    const perms = (data as any)?.myPermissions?.permissions;
    if (!perms) return;
    const newMap = new Map<string, number>();
    perms.forEach(({ menuCode, permissionBit }: { menuCode: string; permissionBit: number }) => {
      newMap.set(menuCode, permissionBit);
    });
    permMap.current = newMap;
    setLoaded(true);
  }, [data]);

  // When the user logs out, isAuthenticated flips to false → query is skipped
  // → clear the map so a new login doesn't see stale permissions.
  React.useEffect(() => {
    if (!isAuthenticated) {
      permMap.current = new Map();
      setLoaded(false);
    }
  }, [isAuthenticated]);

  const hasPermission = useCallback((menuCode: string, mode: FormMode): boolean => {
    // In development with no token the query is skipped but dev fallback gives full access.
    // We show everything when !isAuthenticated+!loaded to avoid locking out the dev fallback.
    if (!isAuthenticated) return true;
    if (!loaded) return false;   // still loading — PermissionRoute shows a spinner
    const bit = permMap.current.get(menuCode);
    if (bit === undefined) return false;
    return (bit & FORM_MODE_BITS[mode]) !== 0;
  }, [isAuthenticated, loaded]);

  const refetch = useCallback(() => {
    setLoaded(false);
    apolloRefetch();
  }, [apolloRefetch]);

  const isLoading = isAuthenticated && (loading || !loaded);

  return (
    <PermissionsContext.Provider value={{ hasPermission, isLoading, refetch }}>
      {children}
    </PermissionsContext.Provider>
  );
};
